import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type LivePayload = {
  version: number;
  changed: boolean;
  scope?: string;
};

const LiveContext = createContext(0);

const WAIT_MS = 25_000;
/** Floor between polls so a server bug cannot spin the browser. */
const MIN_GAP_MS = 1_000;

/**
 * One long-poll for the signed-in app. Pages watch the revision and refetch
 * when the other person (or another device) writes something.
 */
export function LiveProvider({ children }: { children: ReactNode }) {
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    let since: number | null = null;

    const bump = () => setRevision((n) => n + 1);

    async function loop() {
      while (!ac.signal.aborted) {
        const started = Date.now();
        try {
          const q = new URLSearchParams({ wait: String(WAIT_MS) });
          if (since !== null) q.set('since', String(since));
          const res = await fetch(`/api/events?${q}`, {
            credentials: 'same-origin',
            signal: ac.signal,
          });
          if (res.status === 401) return;
          const data = (await res.json()) as LivePayload;
          if (typeof data.version === 'number') since = data.version;
          if (data.changed) bump();
        } catch {
          if (ac.signal.aborted) return;
          await pause(2_000, ac.signal);
          continue;
        }

        const elapsed = Date.now() - started;
        if (elapsed < MIN_GAP_MS) await pause(MIN_GAP_MS - elapsed, ac.signal);
      }
    }

    void loop();

    const onVisible = () => {
      if (document.visibilityState === 'visible') bump();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      ac.abort();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return <LiveContext.Provider value={revision}>{children}</LiveContext.Provider>;
}

export function useLiveReload(reload: () => void): void {
  const revision = useContext(LiveContext);
  const seen = useRef(revision);
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    if (seen.current === revision) return;
    seen.current = revision;
    reloadRef.current();
  }, [revision]);
}

function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}
